import React, { useEffect, useRef } from 'react';
import { motion, useMotionValue, animate } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useLogoLayout } from '../contexts/LogoLayoutContext';

const TRANSITION = { duration: 0.42, ease: [0.22, 1, 0.36, 1] };
const ENTRY_TRANSITION = { duration: 0.55, ease: [0.22, 1, 0.36, 1] };
const FADE_OUT_TRANSITION = { duration: 0.08, ease: [0.4, 0, 1, 1] };
// Short window (≈30ms) to wait for the new page's slot to mount. If no slot
// appears within this window we treat the destination as "no-logo" and fade
// out immediately — prevents the stale-logo flash when navigating to a
// chat thread (or any other slotless page).
const MAX_SLOT_WAIT_FRAMES = 2;

export const FloatingLogo = ({ hidden = false }) => {
  const { getActiveSlot, version } = useLogoLayout();
  const location = useLocation();

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const width = useMotionValue(0);
  const height = useMotionValue(0);
  const opacity = useMotionValue(0);
  const scale = useMotionValue(0.86);

  const transitioningRef = useRef(false);
  const initializedRef = useRef(false);
  const activeAnimationsRef = useRef([]);
  const lastPathRef = useRef(null);

  // Continuously sync position/size to the slot of the *current* route only,
  // so when we navigate to a slot-less page the logo can fade out without
  // waiting for the previous page's slot to unmount.
  useEffect(() => {
    let raf;
    const tick = () => {
      if (!transitioningRef.current && initializedRef.current) {
        const slot = getActiveSlot(location.pathname);
        if (slot) {
          const r = slot.getBoundingClientRect();
          if (r.width > 0 && r.height > 0) {
            x.set(r.left);
            y.set(r.top);
            width.set(r.width);
            height.set(r.height);
          }
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Drive entry + route-change animations. Triggers whenever the active slot
  // identity may have changed (location or slot-stack version).
  useEffect(() => {
    let cancelled = false;
    let frameHandle = null;
    let retries = 0;

    const cancelInflight = () => {
      activeAnimationsRef.current.forEach((c) => c?.stop?.());
      activeAnimationsRef.current = [];
    };

    const animateTo = (rect, isEntry) => {
      const t = isEntry ? ENTRY_TRANSITION : TRANSITION;
      // Snap position/size instantly on entry so the pop-in happens at the
      // correct anchor; for route changes we interpolate position/size.
      if (isEntry) {
        x.set(rect.left);
        y.set(rect.top);
        width.set(rect.width);
        height.set(rect.height);
        const controls = [
          animate(opacity, 1, t),
          animate(scale, 1, t),
        ];
        activeAnimationsRef.current = controls;
        Promise.all(controls.map((c) => c.finished))
          .catch(() => {})
          .finally(() => {
            transitioningRef.current = false;
          });
      } else {
        // Subtle "tap" feedback: scale dips and recovers so every route
        // change produces visible logo motion even when slot positions
        // coincide between pages.
        const controls = [
          animate(x, rect.left, t),
          animate(y, rect.top, t),
          animate(width, rect.width, t),
          animate(height, rect.height, t),
          animate(opacity, 1, t),
          animate(scale, [scale.get(), 0.92, 1], {
            duration: 0.5,
            ease: [0.22, 1, 0.36, 1],
            times: [0, 0.35, 1],
          }),
        ];
        activeAnimationsRef.current = controls;
        Promise.all(controls.map((c) => c.finished))
          .catch(() => {})
          .finally(() => {
            transitioningRef.current = false;
          });
      }
    };

    const tryStart = () => {
      if (cancelled) return;
      const slot = getActiveSlot(location.pathname);
      if (!slot) {
        if (retries < MAX_SLOT_WAIT_FRAMES) {
          retries += 1;
          frameHandle = requestAnimationFrame(tryStart);
          return;
        }
        // New page has no logo slot — fade out fast.
        cancelInflight();
        const c = animate(opacity, 0, FADE_OUT_TRANSITION);
        activeAnimationsRef.current = [c];
        c.finished.catch(() => {}).finally(() => {
          transitioningRef.current = false;
        });
        return;
      }
      const r = slot.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) {
        if (retries < MAX_SLOT_WAIT_FRAMES) {
          retries += 1;
          frameHandle = requestAnimationFrame(tryStart);
          return;
        }
        transitioningRef.current = false;
        return;
      }

      const isEntry = !initializedRef.current;
      initializedRef.current = true;
      lastPathRef.current = location.pathname;
      cancelInflight();
      animateTo(r, isEntry);
    };

    transitioningRef.current = true;
    cancelInflight();
    // Wait one frame so any newly-mounted slot has been laid out.
    frameHandle = requestAnimationFrame(tryStart);

    return () => {
      cancelled = true;
      if (frameHandle) cancelAnimationFrame(frameHandle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, version]);

  return (
    <motion.img
      src="/allure-logo.png"
      alt="Allure"
      data-testid="floating-allure-logo"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        x,
        y,
        width,
        height,
        opacity: hidden ? 0 : opacity,
        scale,
        transformOrigin: 'center center',
        pointerEvents: 'none',
        zIndex: 5,
        objectFit: 'contain',
        willChange: 'transform, width, height',
      }}
    />
  );
};

export default FloatingLogo;
